import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Shield,
  CheckCircle2,
  Clock,
  FileText,
  CreditCard,
  User,
  Loader2,
  AlertCircle,
  Upload,
  XCircle,
  Image
} from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KYCPage = () => {
  const { t } = useTranslation();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [documentType, setDocumentType] = useState('passport');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchKycStatus();
  }, []);

  const fetchKycStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/kyc/status`, { withCredentials: true });
      setKycStatus(res.data);
    } catch (err) {
      // ignore - user might not be logged in
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('kyc.fileTypeError'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('kyc.fileSizeError'));
      return;
    }

    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmitKYC = async () => {
    if (!selectedFile) {
      toast.error(t('kyc.pleaseUpload'));
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('document_type', documentType);
      formData.append('file', selectedFile);

      await axios.post(`${API_URL}/api/kyc/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t('kyc.uploadSuccess'));
      await checkAuth();
      setKycStatus({ status: 'pending', is_verified: false });
    } catch (error) {
      const message = error.response?.data?.detail || t('kyc.uploadError');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const currentStatus = kycStatus?.status || user?.kyc_status || 'not_started';

  if (user?.is_kyc_verified || kycStatus?.is_verified) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-16 bg-[#0A0A0A]">
        <Card className="w-full max-w-md bg-[#141414] border-white/10 rounded-sm">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="font-heading text-2xl text-white mb-2">{t('kyc.verified')}</h2>
            <p className="text-zinc-500 mb-6">{t('kyc.completed')}</p>
            <Button
              className="bg-white text-black hover:bg-zinc-200 rounded-sm"
              onClick={() => navigate('/funds')}
              data-testid="browse-funds"
            >
              {t('kyc.goToFunds')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="font-heading text-3xl text-white mb-2">{t('kyc.title')}</h1>
            <p className="text-zinc-500">{t('kyc.description')}</p>
          </div>

          {/* Steps Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className={`flex items-center ${currentStatus === 'not_started' || currentStatus === 'rejected' ? 'text-white' : 'text-emerald-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                currentStatus === 'not_started' || currentStatus === 'rejected' ? 'border-white bg-white text-black' : 'border-emerald-500 bg-emerald-500 text-white'
              }`}>
                1
              </div>
              <span className="ml-2 text-sm">{t('kyc.stepSelect')}</span>
            </div>
            <div className="w-12 h-0.5 bg-white/20 mx-4" />
            <div className={`flex items-center ${currentStatus === 'pending' ? 'text-amber-500' : currentStatus === 'verified' ? 'text-emerald-500' : 'text-zinc-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                currentStatus === 'pending' ? 'border-amber-500 bg-amber-500 text-black' : 
                currentStatus === 'verified' ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-zinc-600'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm">{t('kyc.reviewing')}</span>
            </div>
            <div className="w-12 h-0.5 bg-white/20 mx-4" />
            <div className={`flex items-center ${currentStatus === 'verified' ? 'text-emerald-500' : 'text-zinc-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                currentStatus === 'verified' ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-zinc-600'
              }`}>
                3
              </div>
              <span className="ml-2 text-sm">{t('kyc.stepDone')}</span>
            </div>
          </div>

          {/* Pending State */}
          {currentStatus === 'pending' && (
            <Card className="bg-[#141414] border-white/10 rounded-sm">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
                <h2 className="font-heading text-2xl text-white mb-2">{t('kyc.reviewing')}</h2>
                <p className="text-zinc-500 mb-4">
                  {t('kyc.reviewingDesc')}
                </p>
                <p className="text-zinc-600 text-sm">
                  {t('kyc.reviewingNote')}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Rejected State */}
          {currentStatus === 'rejected' && (
            <>
              <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-sm mb-6">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-red-400 text-sm font-semibold">{t('kyc.rejected')}</p>
                    {kycStatus?.rejection_reason && (
                      <p className="text-zinc-400 text-xs mt-1">{t('kyc.rejectedReason', { reason: kycStatus.rejection_reason })}</p>
                    )}
                    <p className="text-zinc-400 text-xs mt-1">{t('kyc.rejectedRetry')}</p>
                  </div>
                </div>
              </div>
              <UploadForm
                documentType={documentType}
                setDocumentType={setDocumentType}
                selectedFile={selectedFile}
                previewUrl={previewUrl}
                fileInputRef={fileInputRef}
                handleFileSelect={handleFileSelect}
                handleRemoveFile={handleRemoveFile}
                handleSubmitKYC={handleSubmitKYC}
                loading={loading}
                t={t}
              />
            </>
          )}

          {/* Not Started */}
          {currentStatus === 'not_started' && (
            <UploadForm
              documentType={documentType}
              setDocumentType={setDocumentType}
              selectedFile={selectedFile}
              previewUrl={previewUrl}
              fileInputRef={fileInputRef}
              handleFileSelect={handleFileSelect}
              handleRemoveFile={handleRemoveFile}
              handleSubmitKYC={handleSubmitKYC}
              loading={loading}
              t={t}
            />
          )}

          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            <Card className="bg-[#141414] border-white/10 rounded-sm">
              <CardContent className="p-4 text-center">
                <Shield className="h-6 w-6 text-zinc-500 mx-auto mb-2" />
                <p className="text-zinc-400 text-sm">{t('kyc.secureComm')}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#141414] border-white/10 rounded-sm">
              <CardContent className="p-4 text-center">
                <Clock className="h-6 w-6 text-zinc-500 mx-auto mb-2" />
                <p className="text-zinc-400 text-sm">{t('kyc.fastReview')}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#141414] border-white/10 rounded-sm">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-zinc-500 mx-auto mb-2" />
                <p className="text-zinc-400 text-sm">{t('kyc.oneTime')}</p>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const UploadForm = ({ documentType, setDocumentType, selectedFile, previewUrl, fileInputRef, handleFileSelect, handleRemoveFile, handleSubmitKYC, loading, t }) => (
  <Card className="bg-[#141414] border-white/10 rounded-sm">
    <CardHeader>
      <CardTitle className="text-white">{t('kyc.selectDocument')}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-6">
      <RadioGroup value={documentType} onValueChange={setDocumentType}>
        <div className="space-y-3">
          <Label
            htmlFor="passport"
            className={`flex items-center gap-4 p-4 rounded-sm border cursor-pointer transition-colors ${
              documentType === 'passport' ? 'border-white bg-white/5' : 'border-white/10 hover:border-white/20'
            }`}
          >
            <RadioGroupItem value="passport" id="passport" className="text-white" />
            <FileText className="h-5 w-5 text-zinc-400" />
            <div>
              <p className="text-white">{t('kyc.passport')}</p>
              <p className="text-zinc-500 text-sm">{t('kyc.passport')}</p>
            </div>
          </Label>
          
          <Label
            htmlFor="driver_license"
            className={`flex items-center gap-4 p-4 rounded-sm border cursor-pointer transition-colors ${
              documentType === 'driver_license' ? 'border-white bg-white/5' : 'border-white/10 hover:border-white/20'
            }`}
          >
            <RadioGroupItem value="driver_license" id="driver_license" className="text-white" />
            <CreditCard className="h-5 w-5 text-zinc-400" />
            <div>
              <p className="text-white">{t('kyc.driverLicense')}</p>
              <p className="text-zinc-500 text-sm">{t('kyc.driverLicense')}</p>
            </div>
          </Label>
          
          <Label
            htmlFor="id_card"
            className={`flex items-center gap-4 p-4 rounded-sm border cursor-pointer transition-colors ${
              documentType === 'id_card' ? 'border-white bg-white/5' : 'border-white/10 hover:border-white/20'
            }`}
          >
            <RadioGroupItem value="id_card" id="id_card" className="text-white" />
            <User className="h-5 w-5 text-zinc-400" />
            <div>
              <p className="text-white">{t('kyc.idCard')}</p>
              <p className="text-zinc-500 text-sm">{t('kyc.idCard')}</p>
            </div>
          </Label>
        </div>
      </RadioGroup>

      {/* File Upload Area */}
      <div>
        <Label className="text-zinc-400 mb-2 block">{t('kyc.uploadDoc')}</Label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="kyc-file-input"
        />
        
        {!selectedFile ? (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-white/20 rounded-sm p-8 text-center cursor-pointer hover:border-white/40 transition-colors"
            data-testid="kyc-upload-area"
          >
            <Upload className="h-8 w-8 text-zinc-500 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">{t('kyc.clickToSelect')}</p>
            <p className="text-zinc-600 text-xs mt-1">{t('kyc.fileTypes')}</p>
          </div>
        ) : (
          <div className="border border-white/10 rounded-sm p-4" data-testid="kyc-file-preview">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" className="w-16 h-16 object-cover rounded" />
                ) : (
                  <div className="w-16 h-16 bg-white/5 rounded flex items-center justify-center">
                    <FileText className="h-6 w-6 text-zinc-500" />
                  </div>
                )}
                <div>
                  <p className="text-white text-sm">{selectedFile.name}</p>
                  <p className="text-zinc-500 text-xs">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                data-testid="kyc-remove-file"
              >
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-sm">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-amber-400 text-sm font-semibold">{t('kyc.notice')}</p>
            <p className="text-zinc-400 text-xs mt-1">
              {t('kyc.notice')}
            </p>
          </div>
        </div>
      </div>

      <Button
        className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm py-6"
        onClick={handleSubmitKYC}
        disabled={loading || !selectedFile}
        data-testid="submit-kyc"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('kyc.submitting')}
          </>
        ) : (
          <>
            <Upload className="mr-2 h-4 w-4" />
            {t('kyc.submit')}
          </>
        )}
      </Button>
    </CardContent>
  </Card>
);

export default KYCPage;
